import { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Eye } from 'lucide-react';
import L from 'leaflet';
import 'leaflet.heat';
import { TROMSO_CENTER, mockSightings } from './mockData';

interface WildlifeSighting {
  id: string;
  species: string;
  count: number;
  location: [number, number];
  timestamp: string;
  reportedBy: string;
  status: 'verified' | 'pending' | 'rare';
  notes?: string;
  image?: string;
}

// HeatmapLayer component
function HeatmapLayer({ data }: { data: { lat: number; lng: number; intensity: number }[] }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    // @ts-ignore (Leaflet.heat extends L but TypeScript doesn't know about it)
    const heatLayer = L.heatLayer(data.map(point => [
      point.lat,
      point.lng,
      point.intensity
    ]), {
      radius: 20,
      blur: 15,
      maxZoom: 10,
      gradient: { 0.4: '#2C5F2D', 0.65: '#97BC62', 1: '#C9E4CA' }
    }).addTo(map);

    return () => {
      map.removeLayer(heatLayer);
    };
  }, [map, data]);

  return null;
}


// Generate heatmap data from sightings
const heatmapData = mockSightings.map(sighting => ({
  lat: sighting.location[0],
  lng: sighting.location[1],
  intensity: sighting.count
}));

export function WildlifeSightings() {
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [timeRange, setTimeRange] = useState('24h');
  const [selectedSpecies, setSelectedSpecies] = useState('all');
  const center: [number, number] = TROMSO_CENTER;

  const getMarkerIcon = (status: WildlifeSighting['status']) => {
    const color = status === 'verified' ? '#2C5F2D' : 
                 status === 'rare' ? '#FF6B6B' : '#FFC107';
    
    return L.divIcon({
      className: 'custom-marker',
      html: `<div class="w-8 h-8 rounded-full bg-white border-2 flex items-center justify-center" style="border-color: ${color}">
        🦘
      </div>`,
      iconSize: [32, 32],
      iconAnchor: [16, 16]
    });
  };

  return (
    <div className="grid grid-cols-[300px_1fr] gap-6">
      {/* Sidebar */}
      <div className="space-y-4">
        {/* Controls */}
        <Card>
          <CardContent className="p-4 space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Time Range</label>
              <Select value={timeRange} onValueChange={setTimeRange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select time range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="24h">Last 24 Hours</SelectItem>
                  <SelectItem value="7d">Last 7 Days</SelectItem>
                  <SelectItem value="30d">Last 30 Days</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Species Filter</label>
              <Select value={selectedSpecies} onValueChange={setSelectedSpecies}>
                <SelectTrigger>
                  <SelectValue placeholder="All Species" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Species</SelectItem>
                  <SelectItem value="kangaroo">Kangaroos</SelectItem>
                  <SelectItem value="koala">Koalas</SelectItem>
                  <SelectItem value="bird">Birds</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Button 
                variant={showHeatmap ? "default" : "outline"}
                onClick={() => setShowHeatmap(!showHeatmap)}
                className="w-full"
              >
                <Eye className="w-4 h-4 mr-2" />
                {showHeatmap ? 'Hide Heatmap' : 'Show Heatmap'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Recent Sightings */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Recent Sightings</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="space-y-2">
              {mockSightings.map(sighting => (
                <div
                  key={sighting.id}
                  className="p-3 rounded-lg hover:bg-gray-100 cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{sighting.species}</span>
                    <Badge variant={
                      sighting.status === 'verified' ? 'default' :
                      sighting.status === 'rare' ? 'destructive' : 'secondary'
                    }>
                      {sighting.status}
                    </Badge>
                  </div>
                  <div className="text-sm mt-1 text-gray-500">
                    {sighting.count} spotted • {new Date(sighting.timestamp).toLocaleTimeString()}
                  </div>
                  <div className="text-sm text-gray-500">
                    Reported by: {sighting.reportedBy}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Map */}
      <Card>
        <CardContent className="p-0">
          <MapContainer
            center={center}
            zoom={13}
            style={{ height: '700px', width: '100%' }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            
            {showHeatmap && (
              <HeatmapLayer data={heatmapData} />
            )}

            {mockSightings.map(sighting => (
              <Marker
                key={sighting.id}
                position={[sighting.location[1], sighting.location[0]]}
                icon={getMarkerIcon(sighting.status as 'verified' | 'pending' | 'rare')}
              >
                <Popup>
                  <div className="p-2">
                    <h4 className="font-semibold">{sighting.species}</h4>
                    <p className="text-sm">Count: {sighting.count}</p>
                    <p className="text-sm">Time: {new Date(sighting.timestamp).toLocaleString()}</p>
                    {sighting.notes && (
                      <p className="text-sm mt-2">{sighting.notes}</p>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </CardContent>
      </Card>
    </div>
  );
}